import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
  Text,
  Box,
  Avatar,
  Badge,
  useColorModeValue,
  Spinner,
  Center,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  useToast,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { FiSearch, FiUser, FiPlus } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import ContactTypeBadges from './ContactTypeBadges';
import { supabase, isSupabaseConfigured, transformContactToCamelCase } from '../../utils/supabase';
import { useVisibilityScope } from '../../hooks/useVisibilityScope';
import { applyVisibilityScope, visibilityScopeKey } from '../../utils/recordVisibility';

/**
 * Contact Picker Component
 * 
 * Reusable contact selector for:
 * - Adding parties to deals
 * - Creating network relationships
 * - Any scenario requiring contact selection
 * 
 * Features:
 * - Search by name, email, phone
 * - Filter by contact type
 * - Show recent contacts
 * - Quick create new contact
 */
export default function ContactPicker({
  isOpen,
  onClose,
  onSelectContact,
  filterByContactTypes = [], // Restrict to certain types
  excludeContactIds = [], // Don't show these contacts
  title = 'Select Contact',
}) {
  const { currentUser } = useAuth();
  const toast = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [recentContacts, setRecentContacts] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loadingRecent, setLoadingRecent] = useState(true);

  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // Multi-owner visibility: the viewer's effective contact book (owned + team
  // book-shares + record shares).
  const { scope: contactScope } = useVisibilityScope({
    entityType: 'contact',
    targetUserId: currentUser?.uid,
  });
  const contactScopeKey = visibilityScopeKey(contactScope) + ':g' + (contactScope?.grantsVersion || 0);

  // FIXED: Stabilize array props to prevent infinite loops
  // Create string signatures to detect content changes
  const excludeIdsSignature = excludeContactIds.join(',');
  const filterTypesSignature = filterByContactTypes.join(',');
  
  const stableExcludeIds = useMemo(() => {
    return excludeContactIds;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excludeIdsSignature]);

  const stableFilterTypes = useMemo(() => {
    return filterByContactTypes;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterTypesSignature]);

  // Load recent contacts on mount
  useEffect(() => {
    if (!isOpen) return;

    const loadRecentContacts = async () => {
      setLoadingRecent(true);
      try {
        if (isSupabaseConfigured()) {
          let query = supabase
            .from('view_contact_summary')
            .select('*')
            .order('last_interaction_at', { ascending: false, nullsFirst: false })
            .limit(10);
          query = applyVisibilityScope(query, contactScope);

          const { data, error } = await query;

          if (error) throw error;

          let contacts = (data || []).map(transformContactToCamelCase);

          // Apply filters
          contacts = contacts.filter(contactData => {
            if (stableExcludeIds.includes(contactData.id)) return false;
            
            if (stableFilterTypes.length > 0) {
              const hasMatchingType = contactData.contactTypes?.some(type => 
                stableFilterTypes.includes(type)
              );
              if (!hasMatchingType) return false;
            }
            return true;
          });

          setRecentContacts(contacts);
        }
      } catch (error) {
        console.error('Error loading recent contacts:', error);
      } finally {
        setLoadingRecent(false);
      }
    };

    loadRecentContacts();
    // contactScopeKey covers contactScope content (incl. grant versions)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentUser.uid, stableExcludeIds, stableFilterTypes, contactScopeKey]);

  // Search contacts
  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    // Only search when user has typed at least 3 characters (performance optimization)
    if (searchTerm.trim().length < 3) {
      setSearchResults([]);
      return;
    }

    const searchContacts = async () => {
      setSearching(true);
      try {
        if (isSupabaseConfigured()) {
          const searchLower = searchTerm.toLowerCase().trim();
          
          // Use Postgres full-text search or ilike for flexible matching
          let searchQuery = supabase
            .from('view_contact_summary')
            .select('*')
            .or(`first_name.ilike.%${searchLower}%,last_name.ilike.%${searchLower}%,email.ilike.%${searchLower}%,phone.ilike.%${searchLower}%`)
            .limit(50);
          searchQuery = applyVisibilityScope(searchQuery, contactScope);
          const { data, error } = await searchQuery;

          if (error) throw error;

          let contacts = (data || []).map(transformContactToCamelCase);

          // Apply additional filters
          contacts = contacts.filter(contactData => {
            // Exclude specific contacts
            if (stableExcludeIds.includes(contactData.id)) return false;
            
            // Filter by contact type if specified
            if (stableFilterTypes.length > 0) {
              const hasMatchingType = contactData.contactTypes?.some(type => 
                stableFilterTypes.includes(type)
              );
              if (!hasMatchingType) return false;
            }

            return true;
          });

          // Limit final results
          setSearchResults(contacts.slice(0, 20));
        }
      } catch (error) {
        console.error('Error searching contacts:', error);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    };

    // Increased debounce to 500ms to reduce query frequency
    const debounceTimer = setTimeout(searchContacts, 500);
    return () => clearTimeout(debounceTimer);
    // contactScopeKey covers contactScope content (incl. grant versions)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, currentUser.uid, stableFilterTypes, stableExcludeIds, contactScopeKey]);

  const handleContactSelect = (contact) => {
    if (onSelectContact) {
      onSelectContact(contact);
    }
    onClose();
  };

  const ContactItem = ({ contact }) => (
    <Box
      p={3}
      borderRadius="md"
      borderWidth="1px"
      borderColor={borderColor}
      _hover={{ bg: hoverBg, cursor: 'pointer' }}
      onClick={() => handleContactSelect(contact)}
    >
      <HStack spacing={3}>
        <Avatar
          size="sm"
          name={`${contact.firstName} ${contact.lastName}`}
        />
        <VStack align="flex-start" spacing={0} flex={1}>
          <Text fontWeight="semibold" fontSize="sm">
            {contact.firstName} {contact.lastName}
          </Text>
          <ContactTypeBadges
            contactTypes={contact.contactTypes || []}
            primaryContactType={contact.primaryContactType}
            size="xs"
            maxDisplay={2}
          />
          <HStack spacing={3} mt={1}>
            {contact.email && (
              <Text fontSize="xs" color="gray.600">{contact.email}</Text>
            )}
            {contact.phone && (
              <Text fontSize="xs" color="gray.600">{contact.phone}</Text>
            )}
          </HStack>
        </VStack>
      </HStack>
    </Box>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Search */}
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <Icon as={FiSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
            </InputGroup>

            {filterByContactTypes.length > 0 && (
              <Alert status="info" size="sm">
                <AlertIcon />
                <Text fontSize="xs">
                  Filtering for: {filterByContactTypes.join(', ')}
                </Text>
              </Alert>
            )}

            {/* Results */}
            <Tabs size="sm" colorScheme="blue">
              <TabList>
                <Tab>
                  Search Results {searchResults.length > 0 && (
                    <Badge ml={2} colorScheme="blue">{searchResults.length}</Badge>
                  )}
                </Tab>
                <Tab>
                  Recent Contacts {recentContacts.length > 0 && (
                    <Badge ml={2} colorScheme="gray">{recentContacts.length}</Badge>
                  )}
                </Tab>
              </TabList>

              <TabPanels>
                <TabPanel px={0}>
                  <VStack align="stretch" spacing={2} maxH="400px" overflowY="auto">
                    {searching ? (
                      <Center py={8}>
                        <Spinner size="sm" />
                      </Center>
                    ) : searchTerm && searchTerm.trim().length < 3 ? (
                      <Center py={8}>
                        <Text color="gray.500" fontSize="sm">
                          Type at least 3 characters to search...
                        </Text>
                      </Center>
                    ) : searchTerm && searchResults.length === 0 ? (
                      <Center py={8}>
                        <VStack>
                          <Icon as={FiUser} boxSize={10} color="gray.300" />
                          <Text color="gray.500" fontSize="sm">No contacts found</Text>
                          <Button
                            size="sm"
                            leftIcon={<FiPlus />}
                            colorScheme="blue"
                            variant="outline"
                            mt={2}
                            onClick={() => {
                              // TODO: Open QuickAddContact with pre-filled search term
                              toast({
                                title: 'Create Contact',
                                description: 'Coming soon - create from search',
                                status: 'info',
                                duration: 2000,
                              });
                            }}
                          >
                            Create "{searchTerm.split(' ')[0]}"
                          </Button>
                        </VStack>
                      </Center>
                    ) : searchResults.length > 0 ? (
                      searchResults.map(contact => (
                        <ContactItem key={contact.id} contact={contact} />
                      ))
                    ) : (
                      <Center py={8}>
                        <Text color="gray.500" fontSize="sm">
                          Start typing to search contacts...
                        </Text>
                      </Center>
                    )}
                  </VStack>
                </TabPanel>

                <TabPanel px={0}>
                  <VStack align="stretch" spacing={2} maxH="400px" overflowY="auto">
                    {loadingRecent ? (
                      <Center py={8}>
                        <Spinner size="sm" />
                      </Center>
                    ) : recentContacts.length === 0 ? (
                      <Center py={8}>
                        <Text color="gray.500" fontSize="sm">
                          No recent contacts
                        </Text>
                      </Center>
                    ) : (
                      recentContacts.map(contact => (
                        <ContactItem key={contact.id} contact={contact} />
                      ))
                    )}
                  </VStack>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

